import ky from 'ky';
import Notification from '@Common/Notification';
import { SUBSCRIPTION_HEADER_NAME } from '@Constants/constants';
import { API_TIMEOUT } from '@Helpers/apiConfig';
import { refreshAccessToken } from '@Helpers/tokenRefresh';

// Transient failures worth retrying; every other error status fails on the first attempt.
// 404 is intentionally NOT here — a real "not found" is a terminal answer, not a
// transient blip, and retrying it 3x only adds latency before the same result.
const RETRYABLE_STATUS_CODES = [408, 429, 504];

let _store;

export const pacerApiInjectStore = store => {
    _store = store;
}

/**
 * Thrown when a request 401s and the token refresh also fails (session truly
 * expired or revoked). Carries a user-facing message so that if it momentarily
 * reaches an ErrorBoundary before the login redirect completes, the user sees a
 * sign-out notice rather than a schema-validation error.
 */
export class SessionExpiredError extends Error {
    constructor() {
        super('Your session has expired. Redirecting to sign in…');
        this.name = 'SessionExpiredError';
    }
}

export const createPacerApi = () => {
    if (!_store) {
        throw new Error(
            'pacerApi: Store has not been injected. Please call pacerApiInjectStore(store) before using createPacerApi().'
        );
    }
    const state = _store.getState();
    const token = state.user.accessToken;
    const subscriptionId = state.user.currentSubscriptionId;

    return ky.extend({
        timeout: API_TIMEOUT,
        retry: {
            limit: 2, // Retries 2 times (after initial failure)
            statusCodes: RETRYABLE_STATUS_CODES,
            // delay: 1000, // Constant 1 second delay (overrides exponential)
            // backoffLimit: 30000, // Max delay of 30 seconds
            // jitter: true, // Add random jitter
            retryOnTimeout: true,
        },
        throwHttpErrors: false, // We'll handle HTTP errors manually (showing notifications, etc.)
        hooks: {
            beforeRequest: [
                (request, options, { retryCount }) => {
                    // Only set default auth header on initial request, not on retries (retries may have refreshed token set by beforeRetry)
                    if (retryCount === 0) {
                        request.headers.set('Authorization', `Bearer ${token}`);
                    }

                    if (subscriptionId) {
                        request.headers.set(SUBSCRIPTION_HEADER_NAME, subscriptionId.toString());
                    }
                }
            ],
            afterResponse: [
                // Retry with a fresh token on a 401 error; log out if refresh fails
                async (request, _options, response, state) => {
                    if (response.status === 401 && state.retryCount === 0) {
                        const newToken = await refreshAccessToken(_store);
                        if (newToken) {
                            const headers = new Headers(request.headers);
                            headers.set('Authorization', `Bearer ${newToken}`);
                            return fetch(new Request(request, { headers }));
                        }
                        /**
                         * Refresh failed: the session is over. LOGOUT_SUCCESS was
                         * dispatched (resetEnhancer clears redux, PrivateRoute redirects
                         * to login). Throw a clean auth error so the raw 401 body never
                         * reaches a zod `.parse()` — otherwise the empty/string body
                         * surfaces as "Invalid input: expected object, received string"
                         * in the ErrorBoundary instead of a graceful sign-out.
                         */
                        throw new SessionExpiredError();
                    }
                },

                // Show a notification for non-success status codes (401 is handled above)
                async (request, options, response, state) => {
                    if (response.status === 401 || response.ok) return;

                    /**
                     * Wait for the final attempt only when this status is one ky actually
                     * retries. Anything else (400, 403, 500, …) fails on the first attempt,
                     * so gating on retryCount would silence it completely.
                     */
                    const retry = options && options.retry;
                    const maxRetries = (retry && typeof retry.limit === 'number') ? retry.limit : 2;
                    const willRetry = RETRYABLE_STATUS_CODES.includes(response.status) && state.retryCount < maxRetries;
                    if (willRetry) return;

                    console.error(`API request error: ${request.method} ${request.url} - Status: ${response.status} - Retry Count: ${state.retryCount}`);

                    let body: Record<string, unknown> = {};
                    try {
                        body = await response.clone().json();
                    } catch {
                        // Non-JSON error body (HTML error page, empty response, …)
                    }
                    const message = typeof body.message === 'string' && body.message
                        ? body.message
                        : 'An unexpected error occurred.';
                    Notification('error', 'An Error Occurred', message);

                    /**
                     * Stamp the failure into the body. parseMutationResponse only ever sees the
                     * parsed body, so an error payload that carries neither `isError` nor `status`
                     * (e.g. `{ message: "..." }` on a 400) would otherwise be read as a success —
                     * reporting "saved" for a request the server rejected.
                     */
                    return new Response(
                        JSON.stringify({
                            ...body,
                            isError: true,
                            status: response.status,
                            errors: Array.isArray(body.errors) && body.errors.length ? body.errors : [{ message }],
                        }),
                        { status: response.status, headers: { 'Content-Type': 'application/json' } }
                    );
                }
            ]
        }
    });
}
